import { type CSSProperties, type FormEvent, useState } from 'react'
import { Check, ChevronDown, X } from 'lucide-react'
import { DIMENSION_LABELS, type RangeSelection, type ShopifyFilterGroup } from './storefront-api'
import {
  displayFilterLabel,
  formatCount,
  OPEN_BY_DEFAULT,
  optionAvailability,
  sanitizeRangeValue,
  usableOptions,
} from './storefront-format'
import { activeFilterCount, type DiscoveryState } from './search-state'

// A filter chosen outside the sidebar (smart filters) may not be among its options.
function slugLabel(value: string) {
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)+$/.test(value)) return value
  return value
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function FilterOptionGroup({
  group,
  selectedValues,
  disabled,
  onToggle,
}: {
  group: ShopifyFilterGroup
  selectedValues: string[]
  disabled: boolean
  onToggle: (key: string, value: string) => void
}) {
  const label = displayFilterLabel(group)
  return (
    <details className="sf-facet" open={OPEN_BY_DEFAULT.has(group.key)}>
      <summary className="sf-facet__summary">
        <span>
          {label}
          {selectedValues.length > 0 ? ` (${selectedValues.length})` : ''}
        </span>
        <ChevronDown className="sf-facet__chevron" size={18} strokeWidth={1.5} aria-hidden="true" />
      </summary>
      <fieldset>
        <legend className="sr-only">{label}</legend>
        <ul className="sf-facet__options">
          {usableOptions(group).map((option) => {
            const checked = selectedValues.includes(option.value)
            const count = optionAvailability(option)
            const unavailable = count === 0 && !checked
            return (
              <li key={option.value}>
                <label className={`sf-facet__option ${unavailable ? 'is-unavailable' : ''}`}>
                  <span className="sf-facet__option-main">
                    <span className={`sf-checkbox ${checked ? 'is-checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled || unavailable}
                        onChange={() => onToggle(group.key, option.value)}
                        aria-label={option.label}
                      />
                      <Check size={12} strokeWidth={3} aria-hidden="true" />
                    </span>
                    <span className="sf-facet__option-label">{option.label}</span>
                  </span>
                  {count !== null ? (
                    <span className="sf-facet__count">{formatCount(count)}</span>
                  ) : null}
                </label>
              </li>
            )
          })}
        </ul>
      </fieldset>
    </details>
  )
}

/** Ship time: a slider over the ship times the matched products have; the far right is any time. */
function ShipDaysFilterGroup({
  group,
  selection,
  onApply,
}: {
  group: ShopifyFilterGroup
  selection: RangeSelection
  onApply: (key: string, selection: RangeSelection) => void
}) {
  const label = displayFilterLabel(group)
  const stops = usableOptions(group)
    .map((option) => ({ ...option, days: Number(option.value) }))
    .filter((stop) => Number.isFinite(stop.days))
    .sort((left, right) => left.days - right.days)
  const last = stops.length - 1
  // While dragging, the thumb shows the draft; the applied limit takes over once committed.
  const [draft, setDraft] = useState<number | null>(null)
  const selected = stops.findIndex((stop) => stop.value === selection.max)
  const index = draft ?? (selected >= 0 ? selected : last)
  const stop = stops[Math.min(index, last)]
  if (!stop) return null
  const any = index >= last
  const reading = any ? 'Any' : stop.label
  const commit = () => {
    if (draft === null) return
    setDraft(null)
    onApply(group.key, any ? {} : { max: stop.value })
  }
  const sliderStyle = {
    '--range-min': '0%',
    '--range-max': `${last ? (index / last) * 100 : 100}%`,
  } as CSSProperties

  return (
    <details className="sf-facet" open={OPEN_BY_DEFAULT.has(group.key)}>
      <summary className="sf-facet__summary">
        <span>{label}</span>
        <ChevronDown className="sf-facet__chevron" size={18} strokeWidth={1.5} aria-hidden="true" />
      </summary>
      <div className="sf-range">
        <div className="sf-range__reading" aria-hidden="true">
          <span>{reading}</span>
          {optionAvailability(stop) !== null ? (
            <span className="sf-facet__count">{formatCount(optionAvailability(stop) ?? 0)}</span>
          ) : null}
        </div>
        <div className="sf-range__slider" style={sliderStyle}>
          <input
            type="range"
            aria-label={label}
            aria-valuetext={reading}
            value={index}
            min={0}
            max={last}
            step={1}
            onChange={(event) => setDraft(Number(event.target.value))}
            onPointerUp={commit}
            onKeyUp={commit}
          />
        </div>
      </div>
    </details>
  )
}

function RangeFilterGroup({
  group,
  selection,
  disabled,
  onApply,
}: {
  group: ShopifyFilterGroup
  selection: RangeSelection
  disabled: boolean
  onApply: (key: string, selection: RangeSelection) => void
}) {
  const [minimum, setMinimum] = useState(selection.min ?? '')
  const [maximum, setMaximum] = useState(selection.max ?? '')
  const label = displayFilterLabel(group)
  const commit = () =>
    onApply(group.key, {
      ...(minimum.trim() ? { min: minimum.trim() } : {}),
      ...(maximum.trim() ? { max: maximum.trim() } : {}),
    })
  const submit = (event: FormEvent) => {
    event.preventDefault()
    commit()
  }
  const rangeMinimum = Number(group.min ?? 0)
  const rangeMaximum = Number(group.max ?? 0)
  const hasNumericRange = Number.isFinite(rangeMinimum) && Number.isFinite(rangeMaximum)
  const rangeSpan = Math.max(rangeMaximum - rangeMinimum, 1)
  const selectedMinimum = Math.min(
    Math.max(Number(minimum || rangeMinimum), rangeMinimum),
    rangeMaximum,
  )
  const selectedMaximum = Math.max(
    Math.min(Number(maximum || rangeMaximum), rangeMaximum),
    rangeMinimum,
  )
  const sliderStyle = {
    '--range-min': `${((selectedMinimum - rangeMinimum) / rangeSpan) * 100}%`,
    '--range-max': `${((selectedMaximum - rangeMinimum) / rangeSpan) * 100}%`,
  } as CSSProperties

  return (
    <details className="sf-facet" open={OPEN_BY_DEFAULT.has(group.key)}>
      <summary className="sf-facet__summary">
        <span>{label}</span>
        <ChevronDown className="sf-facet__chevron" size={18} strokeWidth={1.5} aria-hidden="true" />
      </summary>
      <form className="sf-range" onSubmit={submit}>
        {group.key === 'price' && hasNumericRange ? (
          <>
            <div className="sf-range__inputs">
              <label>
                <span className="sr-only">From</span>
                <span className="sf-range__currency" aria-hidden="true">
                  $
                </span>
                <input
                  type="number"
                  className="sf-input sf-range__input"
                  value={minimum}
                  min={rangeMinimum}
                  max={rangeMaximum}
                  step="10"
                  disabled={disabled}
                  placeholder={String(Math.floor(rangeMinimum))}
                  onChange={(event) => setMinimum(sanitizeRangeValue(event.target.value))}
                  onBlur={commit}
                />
              </label>
              <label>
                <span className="sr-only">To</span>
                <span className="sf-range__currency" aria-hidden="true">
                  $
                </span>
                <input
                  type="number"
                  className="sf-input sf-range__input"
                  value={maximum}
                  min={rangeMinimum}
                  max={rangeMaximum}
                  step="10"
                  disabled={disabled}
                  placeholder={String(Math.ceil(rangeMaximum))}
                  onChange={(event) => setMaximum(sanitizeRangeValue(event.target.value))}
                  onBlur={commit}
                />
              </label>
            </div>
            <div className="sf-range__slider" style={sliderStyle}>
              <input
                type="range"
                aria-label="From"
                value={minimum || String(rangeMinimum)}
                min={rangeMinimum}
                max={rangeMaximum}
                step="1"
                disabled={disabled}
                onChange={(event) =>
                  setMinimum(
                    String(Math.min(Number(event.target.value), Number(maximum || rangeMaximum))),
                  )
                }
                onPointerUp={commit}
                onKeyUp={commit}
              />
              <input
                type="range"
                aria-label="To"
                value={maximum || String(rangeMaximum)}
                min={rangeMinimum}
                max={rangeMaximum}
                step="1"
                disabled={disabled}
                onChange={(event) =>
                  setMaximum(
                    String(Math.max(Number(event.target.value), Number(minimum || rangeMinimum))),
                  )
                }
                onPointerUp={commit}
                onKeyUp={commit}
              />
            </div>
          </>
        ) : (
          <>
            <div className="sf-range__pair">
              <label>
                <span className="sr-only">Minimum {label.toLowerCase()}</span>
                <input
                  className="sf-input sf-range__input sf-range__input--plain"
                  inputMode="decimal"
                  value={minimum}
                  disabled={disabled}
                  placeholder={group.min == null ? 'Min' : String(group.min)}
                  onChange={(event) => setMinimum(sanitizeRangeValue(event.target.value))}
                />
              </label>
              <label>
                <span className="sr-only">Maximum {label.toLowerCase()}</span>
                <input
                  className="sf-input sf-range__input sf-range__input--plain"
                  inputMode="decimal"
                  value={maximum}
                  disabled={disabled}
                  placeholder={group.max == null ? 'Max' : String(group.max)}
                  onChange={(event) => setMaximum(sanitizeRangeValue(event.target.value))}
                />
              </label>
            </div>
            <div className="sf-range__apply">
              <span>{group.unit ? `Unit: ${group.unit}` : ''}</span>
              <button
                type="submit"
                className="sf-button sf-button--secondary sf-button--small"
                disabled={disabled}
              >
                Apply
              </button>
            </div>
          </>
        )}
      </form>
    </details>
  )
}

export function StorefrontFilters({
  groups,
  state,
  smartNames = {},
  total,
  loading,
  updating,
  error,
  mobileOpen,
  onClose,
  onToggle,
  onRange,
  onClearAll,
}: {
  groups: ShopifyFilterGroup[]
  state: DiscoveryState
  /** Names the smart filter answer gave its filters, by key and value. */
  smartNames?: Record<string, Record<string, string>>
  total: number
  loading: boolean
  updating: boolean
  error: boolean
  mobileOpen: boolean
  onClose: () => void
  onToggle: (key: string, value: string) => void
  onRange: (key: string, selection: RangeSelection) => void
  onClearAll: () => void
}) {
  const selectedCount = activeFilterCount(state)
  const groupsByKey = new Map(groups.map((group) => [group.key, group]))

  return (
    <aside
      className={`sf-facets ${mobileOpen ? 'is-open' : ''}`}
      aria-label="Filters"
      data-e2e="product-filters"
    >
      <div className="sf-facets__mobile-header">
        <span>Filters</span>
        <button type="button" onClick={onClose} aria-label="Close filters">
          <X size={24} strokeWidth={1.5} />
        </button>
      </div>

      {selectedCount ? (
        <div className="sf-active">
          <span className="sf-active__title">Active Filters</span>
          <div className="sf-active__chips">
            {Object.entries(state.multiSelections).flatMap(([key, values]) => {
              const options = groupsByKey.get(key)?.options ?? []
              return values.map((value) => (
                <button
                  type="button"
                  key={`${key}:${value}`}
                  className="sf-active__chip"
                  onClick={() => onToggle(key, value)}
                >
                  <span>
                    {options.find((option) => option.value === value)?.label ??
                      smartNames[key]?.[value] ??
                      slugLabel(value)}
                  </span>
                  <X size={14} aria-hidden="true" />
                  <span className="sr-only">Remove filter</span>
                </button>
              ))
            })}
            {Object.entries(state.rangeSelections).map(([key, selection]) => {
              if (!selection.min && !selection.max) return null
              const group = groupsByKey.get(key)
              const prefix = key === 'price' ? '$' : ''
              const range =
                key === 'ship_days'
                  ? `${selection.max} business days`
                  : `${selection.min ? `${prefix}${selection.min}` : 'Min'} - ${
                      selection.max ? `${prefix}${selection.max}` : 'Max'
                    }`
              return (
                <button
                  type="button"
                  key={key}
                  className="sf-active__chip"
                  onClick={() => onRange(key, {})}
                >
                  <span>
                    {group && key !== 'price' ? `${displayFilterLabel(group)}: ` : ''}
                    {!group && DIMENSION_LABELS[key] ? `${DIMENSION_LABELS[key]}: ` : ''}
                    {range}
                    {!group && DIMENSION_LABELS[key] ? ' in' : ''}
                  </span>
                  <X size={14} aria-hidden="true" />
                  <span className="sr-only">Remove filter</span>
                </button>
              )
            })}
          </div>
          <button
            type="button"
            className="sf-button sf-button--primary sf-button--small sf-active__clear"
            onClick={onClearAll}
          >
            Clear Filter(s)
          </button>
        </div>
      ) : null}

      {error ? (
        <p className="sf-notice sf-notice--error" role="status">
          Filters are temporarily unavailable. Product results can still be browsed.
        </p>
      ) : null}
      {loading ? (
        <div className="sf-facets__loading" role="status" aria-label="Loading filters">
          {Array.from({ length: 5 }, (_, index) => (
            <span key={index} />
          ))}
        </div>
      ) : null}

      <div className={updating ? 'sf-facets__updating' : ''}>
        {groups.map((group) =>
          group.key === 'ship_days' ? (
            <ShipDaysFilterGroup
              key={group.key}
              group={group}
              selection={state.rangeSelections[group.key] ?? {}}
              onApply={onRange}
            />
          ) : group.type === 'range' ? (
            <RangeFilterGroup
              key={`${group.key}:${state.rangeSelections[group.key]?.min ?? ''}:${state.rangeSelections[group.key]?.max ?? ''}`}
              group={group}
              selection={state.rangeSelections[group.key] ?? {}}
              disabled={updating}
              onApply={onRange}
            />
          ) : (
            <FilterOptionGroup
              key={group.key}
              group={group}
              selectedValues={state.multiSelections[group.key] ?? []}
              disabled={updating}
              onToggle={onToggle}
            />
          ),
        )}
      </div>

      {!loading && !error && !groups.length ? (
        <p className="sf-muted">No additional filters are available.</p>
      ) : null}

      <div className="sf-facets__mobile-footer">
        <button type="button" className="sf-button sf-button--primary" onClick={onClose}>
          View {formatCount(total)} products
        </button>
      </div>
    </aside>
  )
}
